/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useHistory, useLocation} from 'react-router-dom'
import {StorefrontPreview} from '@salesforce/commerce-sdk-react/components'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import useActiveData from '../../hooks/use-active-data'
import {useQuery} from '@tanstack/react-query'
import {
    useAccessToken,
    useCategory,
    useShopperBasketsMutation
} from '@salesforce/commerce-sdk-react'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// Chakra
import {
    Box,
    Center,
    Fade,
    Spinner,
    useDisclosure,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {SkipNavLink, SkipNavContent} from '@chakra-ui/skip-nav'

// Contexts
import {CurrencyProvider} from '@salesforce/retail-react-app/app/contexts'
import {MiniCartProvider} from '../../contexts/mini-cart-context'
import {UnavailableProductsInCartProvider} from '../../contexts/unavailable-products-in-cart-context'
import {CustomToastProvider} from '../../contexts/custom-toast-context'
import {SearchModalProvider} from '../../contexts/search-modal-context'
import {GlobalElementsProvider} from '../../contexts/global-elements-context'
import {CategoryUrlMapProvider} from '../../contexts/category-url-context'
import {useGlobalNavigation} from '../../contexts/global-elements-context'

// Local Project Components
import Header from '../header'
import OfflineBanner from '@salesforce/retail-react-app/app/components/offline-banner'
import OfflineBoundary from '@salesforce/retail-react-app/app/components/offline-boundary'
import ScrollToTop from '../scroll-to-top'
import Footer from '../footer'
import loadable from '@loadable/component'

// Lazy load components only used on checkout (not needed on homepage)
const CheckoutHeader = loadable(() => import('../../pages/checkout/partials/checkout-header'), {
    ssr: false
})
const CheckoutFooter = loadable(() => import('../../pages/checkout/partials/checkout-footer'), {
    ssr: false
})

// Lazy load DrawerMenu - only needed when mobile menu opens (~80-100KB framer-motion savings)
const DrawerMenu = loadable(() => import('../drawer-menu'), {ssr: false})
import {ListMenu, ListMenuContent} from '../../components/list-menu'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import StoreLocatorModal from '@salesforce/retail-react-app/app/components/store-locator-modal'
import CustomToast from '../custom-toast'
import TopBar from '../top-bar'
// Lazy load AccountModal - only used when user clicks account button (not on initial page load)
const AccountModal = loadable(() => import('../account-modal'), {
    ssr: false
})
import {useAccountModal} from '../account-modal/useAccountModal'
import Script from '../script'
import Optimizely from '../optimizely'

// Hooks
import {useAuthModal} from '../../hooks/use-auth-modal'
// Lazy load AuthModal - only needed when user clicks login
const AuthModal = loadable(
    () => import('../../hooks/use-auth-modal').then((m) => ({default: m.AuthModal})),
    {
        ssr: false
    }
)
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useUpdateShopperContext} from '../../hooks/use-update-shopper-context'
import {useClaraChat} from '../../hooks/use-clara-chat'
import {HeaderProvider} from '../../contexts/header-context'
import {StickyProvider} from '../../contexts/sticky-context'
import {AccountEditProvider} from '../../contexts/account-edit-context'
import {useOptimizelyPage} from '../../hooks/use-optimizely-page'
import useDetectDevice from '../../hooks/use-detect-device'
import PreviewModeProjectSelector from '../preview-mode-project-selector'
import {useDNT} from '@salesforce/commerce-sdk-react'

// HOCs
import {withCommerceSdkReact} from '@salesforce/retail-react-app/app/components/with-commerce-sdk-react/with-commerce-sdk-react'

// Localization
import {IntlProvider} from 'react-intl'

// Others
import {
    watchOnlineStatus,
    flatten,
    isServer,
    getSessionJSONItem,
    clearSessionJSONItem
} from '@salesforce/retail-react-app/app/utils/utils'
import {getTargetLocale, fetchTranslations} from '@salesforce/retail-react-app/app/utils/locale'
import {
    HOME_HREF,
    THEME_COLOR,
    CAT_MENU_DEFAULT_NAV_SSR_DEPTH,
    DEFAULT_LOCALE,
    ACTIVE_DATA_ENABLED
} from '../../constants'

import Seo from '../../components/seo'
import {Helmet} from 'react-helmet'
import {
    mergeCMSCategoryReplacements,
    transformCMSMenuToCategories
} from '../../components/optimizely/utils/utils'

// Import a helper method from a new brandUtils.js
import {getRootCategoryId} from '../../utils/brandUtils'
import {GoogleReCaptchaProvider} from 'react-google-recaptcha-v3'

// GTM
import {GTMScript} from '../../utils/gtm-script'
import {
    sendUserSession,
    sendLoginEvent,
    sendSignUpEvent,
    sendClickEvent,
    toSHA256
} from '../../utils/gtm-utils'

import {BrazeScript} from '../../utils/braze-script'
import {LogRocketScript} from '../../utils/logrocket-script'
import {SignifydScript} from '../../utils/signifyd-script'

const PlaceholderComponent = () => (
    <Center p="2">
        <Spinner size="lg" />
    </Center>
)

// DIDOMI - Lazy load since it's conditionally rendered based on IS_DIDOMI_ENABLED_GARAGE
const DidomiSDK = loadable(() => import('@didomi/react').then((m) => ({default: m.DidomiSDK})), {
    ssr: false
})

const ListMenuContentWithData = withCommerceSdkReact(
    ({data, ...rest}) => {
        const {data: globalNavigationData} = useGlobalNavigation()

        // Merge category replacements from CMS to category object
        // Start with API data and merge CMS data on top
        let categoriesWithCMSData = mergeCMSCategoryReplacements(data, globalNavigationData)

        const apiData = {
            category: data
        }
        if (categoriesWithCMSData?.ShopMenuContent) {
            // Transform the CMS data to category object models to can render the entire popover from CMS
            categoriesWithCMSData = transformCMSMenuToCategories(
                [categoriesWithCMSData?.ShopMenuContent],
                apiData
            )
        }

        // If rest.item already has CMS mega menu data (from list-menu.jsx), preserve it
        // This handles the case where the CMS data was merged at a higher level
        if (rest?.item?.hasCMSMegaMenu && rest?.item?.CMSMegaMenuData) {
            categoriesWithCMSData = {
                ...categoriesWithCMSData,
                hasCMSMegaMenu: rest.item.hasCMSMegaMenu,
                CMSMegaMenuData: rest.item.CMSMegaMenuData,
                CategoryReplacement: rest.item.CategoryReplacement
            }
        }

        // Flatten the deeply-fetched category data and merge into categories map
        // This ensures nested categories have their c_relativeUrl available
        const deepCategories = flatten(data || {}, 'categories')
        const mergedCategories = {...rest?.categories, ...deepCategories}

        // Rest contains also categories which needs to be merged as well
        Object.keys(mergedCategories).forEach((key) => {
            mergeCMSCategoryReplacements(mergedCategories[key], globalNavigationData)
        })

        return (
            <Fade in={true}>
                <ListMenuContent {...rest} item={categoriesWithCMSData} categories={mergedCategories} />
            </Fade>
        )
    },
    {
        hook: useCategory,
        queryOptions: ({item}) => ({
            parameters: {
                id: item.id,
                levels: 2
            }
        }),
        placeholder: PlaceholderComponent
    }
)

const App = (props) => {
    const {isMacOSChrome} = useDetectDevice()
    const {site, locale, buildUrl} = useMultiSite()
    const {app: appConfig} = getConfig()

    // DNT
    const {selectedDnt, updateDNT} = useDNT()

    // Maintenance mode check - must be at the top to disable unnecessary API calls
    const isMaintenanceEnabled =
        appConfig.MAINTENANCE_GLOBAL_WEB_ENABLED === 'true' &&
        appConfig.MAINTENANCE_GLOBAL_WEB_PAGE_ID &&
        appConfig.MAINTENANCE_GLOBAL_WEB_PAGE_ID !== ''

    const [isDidomiReady, setIsDidomiReady] = useState(
        appConfig.IS_DIDOMI_ENABLED_GARAGE !== 'true'
    )
    const normalizedLang = (locale?.id).replace('_', '-')

    /*-------- Country Selector: START --------*/
    const [showCountrySelectorOnFooter, setShowCountrySelectorOnFooter] = useState(false)

    const handleShowCountrySelectorOnFooter = (showCSOnFooter) => {
        setShowCountrySelectorOnFooter(showCSOnFooter)
    }
    /*-------- Country Selector: END --------*/

    /*-------- Customization to get brand root category: START --------*/
    const ROOT_CATEGORY_GARAGE = getRootCategoryId(site.id, 'garage')
    /*-------- Customization to get brand root category: END --------*/

    const {children} = props
    const {data: categoriesTree} = useCategory({
        parameters: {id: ROOT_CATEGORY_GARAGE, levels: CAT_MENU_DEFAULT_NAV_SSR_DEPTH},
        enabled: !isMaintenanceEnabled
    })
    const categories = flatten(categoriesTree || {}, 'categories')
    const {getTokenWhenReady} = useAccessToken()
    const appOrigin = useAppOrigin()
    const activeData = useActiveData()
    const history = useHistory()
    const location = useLocation()
    const authModal = useAuthModal()
    const accountModalProps = useAccountModal()

    const [isSignifydReady, setSignifydReady] = useState(false)
    const [isOnline, setIsOnline] = useState(true)
    const styles = useStyleConfig('App')
    const {isOpen, onOpen, onClose} = useDisclosure()
    const {
        isOpen: isOpenStoreLocator,
        onOpen: onOpenStoreLocator,
        onClose: onCloseStoreLocator
    } = useDisclosure()

    const targetLocale = getTargetLocale({
        getUserPreferredLocales: () => {
            // CONFIG: This function should return an array of preferred locales. They can be
            // derived from various sources. Below are some examples of those:
            //
            // - client side: window.navigator.languages
            // - the page URL they're on (example.com/en-GB/home)
            // - cookie (if their previous preference is saved there)
            //
            // If this function returns an empty array (e.g. there isn't locale in the page url),
            // then the app would use the default locale as the fallback.

            // NOTE: Your implementation may differ, this is just what we did.
            return [locale?.id || DEFAULT_LOCALE]
        },
        l10nConfig: site.l10n
    })

    // If the translation file exists, it'll be served directly from static folder (and won't reach this code here).
    // However, if the file is missing, the App would render a 404 page.
    const is404ForMissingTranslationFile = /\/static\/translations\/compiled\/[^.]+\.json$/.test(
        location?.pathname
    )

    // Fetch the translation message data using the target locale.
    const {data: messages} = useQuery({
        queryKey: ['app', 'translations', 'messages', targetLocale],
        queryFn: () => {
            if (is404ForMissingTranslationFile) {
                // Return early to prevent an infinite loop
                // Otherwise, it'll continue to fetch the missing translation file again
                return {}
            }
            return fetchTranslations(targetLocale, appOrigin)
        },
        enabled: isServer
    })

    // Used to conditionally render header/footer for checkout page
    const isCheckout = /\/checkout$/.test(location?.pathname) || /\/esw-checkout$/.test(location?.pathname)

    // Initialize Clara Chat only when not on checkout page and not in maintenance mode
    useClaraChat({enabled: !isCheckout && !isMaintenanceEnabled})

    const {l10n} = site
    // Get the current currency to be used through out the app
    const currency = locale.preferredCurrency || l10n.defaultCurrency

    // Handle creating a new basket if there isn't one already assigned to the current
    // customer.
    const {data: customer} = useCurrentCustomer()
    const {data: basket} = useCurrentBasket()

    const updateBasket = useShopperBasketsMutation('updateBasket')
    const updateCustomerForBasket = useShopperBasketsMutation('updateCustomerForBasket')

    const [brazeUserId, setBrazeUserId] = useState(null)
    const [logRocketUserId, setLogRocketUserId] = useState(null)

    useEffect(() => {
        // update the basket currency if it doesn't match the current locale currency
        if (isMaintenanceEnabled) return
        if (basket?.currency && basket?.currency !== currency) {
            updateBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: {currency}
            })
        }
    }, [basket?.currency, isMaintenanceEnabled])

    useEffect(() => {
        // update the basket customer email
        if (isMaintenanceEnabled) return
        if (
            basket &&
            customer?.isRegistered &&
            customer?.email &&
            customer?.email !== basket?.customerInfo?.email
        ) {
            updateCustomerForBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: {
                    email: customer.email
                }
            })
        }
    }, [customer?.isRegistered, customer?.email, basket?.customerInfo?.email, isMaintenanceEnabled])

    // GTM Modification
    useEffect(() => {
        if (customer?.email) {
            let method = 'email_password'

            if (getSessionJSONItem('from-social')) {
                method = 'social'
                clearSessionJSONItem('from-social')
            }

            if (getSessionJSONItem('from-registration')) {
                clearSessionJSONItem('from-registration')
                sendSignUpEvent(customer, method)
            } else if (getSessionJSONItem('from-login')) {
                clearSessionJSONItem('from-login')
                sendLoginEvent(customer, method)
            }
        }
    }, [customer?.email])

    useEffect(() => {
        // Listen for online status changes.
        watchOnlineStatus((isOnline) => {
            setIsOnline(isOnline)
        })
    }, [])

    /**
     * Adds the 'is-mac-chrome' CSS class to the root HTML element when the application is running
     * in Google Chrome on macOS. This effect runs whenever the 'isMacOSChrome' dependency changes,
     * ensuring the class is present only in the specified environment for platform-specific styling.
     *
     * @param {boolean} isMacOSChrome - Indicates whether the user agent is Google Chrome on macOS.
     */
    useEffect(() => {
        if (isMacOSChrome) {
            document.documentElement.classList.add('is-mac-chrome')
        }
    }, [isMacOSChrome])

    useEffect(() => {
        document.documentElement.classList.add('js-enabled')
    }, [])

    // Handle updating the shopper context
    useUpdateShopperContext()

    useEffect(() => {
        // Lets automatically close the mobile navigation when the
        // location path is changed.
        onClose()
    }, [location])

    const onLogoClick = () => {
        // Goto the home page.
        const path = buildUrl(HOME_HREF)

        history.push(path)

        // Close the drawer.
        onClose()
    }

    const onCartClick = () => {
        const path = buildUrl('/cart')
        history.push(path)

        // Close the drawer.
        onClose()
    }

    const onAccountClick = () => {
        accountModalProps.onOpen()
        // GTM Modification
        sendClickEvent('prompt_sign_in_clicked')
    }

    const onWishlistClick = () => {
        const wishlistPath = buildUrl('/account/wishlist')

        if (customer?.isRegistered) {
            history.push(wishlistPath)
            return
        }

        // GTM Modification
        sendClickEvent('wishlist_cta_clicked')
        authModal.setIsWishlistClick(true)
        authModal.onOpen()

        history.push({
            state: {
                from: {pathname: wishlistPath}
            }
        })
    }

    const onLoyaltyClick = () => {
        // TODO FD: Update the url after the implementation will be finished
        let path = buildUrl('/my-rewards')

        if (!customer?.isRegistered) {
            path = buildUrl('/account')
        }

        history.push(path)
    }

    const onGiftCardClick = () => {
        const path = buildUrl('/p/e-gift-card/GARGIFTCARDGBEGC001.html')
        history.push(path)
    }

    const trackPage = () => {
        activeData.trackPage(site.id, locale.id, currency)
    }

    useEffect(() => {
        trackPage()
        // GTM Modification
        if (customer?.customerType) {
            sendUserSession(customer, location, locale)
        }
    }, [location.pathname, isDidomiReady, customer?.email])

    useEffect(() => {
        if (basket?.c_SignifydOrderSessionId) {
            setSignifydReady(true)
        }
    }, [basket?.c_SignifydOrderSessionId])

    // Hook must be called unconditionally - pass null to disable fetch when maintenance is off
    const {data: maintenancePageData, isLoading: isMaintenanceLoading} = useOptimizelyPage(
        isMaintenanceEnabled ? appConfig.MAINTENANCE_GLOBAL_WEB_PAGE_ID : null
    )
    // Handle Didomi accept notice submission
    const handleDidomiConsentChanged = (cwtToken) => {
        // Check if the user has consented to Einstein (CQuotient) and update SFCC's DNT cookie accordingly.
        const isEienstienAuthorizedbyUser = Object.values(
            Didomi?.getCurrentUserStatus()?.vendors ?? {}
            ).some(
            vendor =>
                typeof vendor.id === 'string' &&
                vendor.id.includes('cquotient') &&
                vendor.enabled
            )
        updateDNT(!isEienstienAuthorizedbyUser)
    }

    // Render maintenance page if enabled
    // Note: We don't check for !maintenancePageData anymore to allow SSR to work
    // The Optimizely component handles the loading state gracefully
    if (isMaintenanceEnabled) {
        return (
            <IntlProvider
                onError={(err) => {
                    if (!messages) {
                        return
                    }
                    if (err.code === 'MISSING_TRANSLATION') {
                        logger.warn('Missing translation', {
                            namespace: 'App.IntlProvider',
                            additionalProperties: {
                                errorMessage: err.message
                            }
                        })
                        return
                    }
                    throw err
                }}
                locale={targetLocale}
                messages={messages}
                defaultLocale={DEFAULT_LOCALE}
            >
                <Helmet>
                    <meta name="robots" content="noindex, nofollow" />
                </Helmet>
                <Box data-testid="maintenance-page" w={'100%'} h={'100vh'}>
                    <Optimizely content={maintenancePageData?.page || maintenancePageData} isLoading={isMaintenanceLoading} />
                </Box>
            </IntlProvider>
        )
    }

    const isBrazeReady = () =>
        typeof window !== 'undefined' &&
        isDidomiReady &&
        window?.Didomi &&
        (customer?.isGuest || (customer?.isRegistered && customer?.email))

    useEffect(() => {
        if (!customer?.email) {
            setBrazeUserId(`guest-${customer?.customerId}`)
            return
        }

        toSHA256(customer?.email).then(setBrazeUserId)
    }, [customer?.email, customer?.customerId])

    // LogRocket.identify() is append-only per session and cannot be undone, so only
    // registered shoppers with a real email get an identifier; guests stay anonymous.
    useEffect(() => {
        if (!customer?.isRegistered || !customer?.email) {
            setLogRocketUserId(null)
            return
        }

        toSHA256(customer.email).then(setLogRocketUserId)
    }, [customer?.isRegistered, customer?.email])

    return (
        <Box className="sf-app" {...styles.container}>
            {appConfig.IS_DIDOMI_ENABLED_GARAGE === 'true' && (
                <DidomiSDK
                    apiKey={appConfig.DIDOMI_PUBLIC_API_KEY}
                    iabVersion={2}
                    noticeId={appConfig.DIDOMI_NOTICE_ID_GARAGE}
                    gdprAppliesGlobally={true}
                    embedTCFStub={true}
                    onReady={() => setIsDidomiReady(true)}
                    onConsentChanged={handleDidomiConsentChanged}
                />
            )}
            {/* GTM loads after Didomi is ready (or immediately if Didomi is disabled) */}
            {isDidomiReady && <GTMScript />}
            {isBrazeReady() && <BrazeScript brazeUserId={brazeUserId} />}
            {appConfig.IS_LOGROCKET_ENABLED === 'true' && isDidomiReady && (
                <LogRocketScript
                    userId={logRocketUserId}
                    traits={{
                        customerType: customer?.customerType,
                        isRegistered: !!customer?.isRegistered
                    }}
                />
            )}
            {appConfig.AB_TESTING_SCRIPT_SRC && (
                <Script src={appConfig.AB_TESTING_SCRIPT_SRC} delayUntilIdle />
            )}

            <StorefrontPreview getToken={getTokenWhenReady}>
                <GoogleReCaptchaProvider reCaptchaKey={appConfig.RECAPTCHA_SITE_KEY || ''}>
                    <CategoryUrlMapProvider>
                        <AccountEditProvider>
                            <StickyProvider>
                                <HeaderProvider>
                                    <CustomToastProvider>
                                        <Helmet htmlAttributes={{lang: normalizedLang}}>
                                            {/* Keep ≤4 preconnects and only to most important origins. */}
                                            <link rel="preconnect" href="https://dam.dynamiteclothing.com" />
                                            <link rel="dns-prefetch" href="https://www.googletagmanager.com" />
                                            <link rel="dns-prefetch" href="https://www.google.com" />
                                            {/* Preload critical fonts */}
                                            <link
                                                rel="preload"
                                                href={getAssetUrl(
                                                    'static/fonts/HelveticaNeueLTStd-Roman.woff2'
                                                )}
                                                as="font"
                                                type="font/woff2"
                                                crossOrigin="anonymous"
                                            />
                                            <link
                                                rel="preload"
                                                href={getAssetUrl(
                                                    'static/fonts/HelveticaNeueLTStd-Md.woff2'
                                                )}
                                                as="font"
                                                type="font/woff2"
                                                crossOrigin="anonymous"
                                            />
                                            <link
                                                rel="preload"
                                                href={getAssetUrl(
                                                    'static/fonts/HelveticaNeueLTStd-Bd.woff2'
                                                )}
                                                as="font"
                                                type="font/woff2"
                                                crossOrigin="anonymous"
                                            />
                                            <link
                                                rel="preload"
                                                href={getAssetUrl(
                                                    'static/fonts/GarageNeue-Regular.woff2'
                                                )}
                                                as="font"
                                                type="font/woff2"
                                                crossOrigin="anonymous"
                                            />
                                            {/* Preload GTM script - downloads in parallel with Didomi */}
                                            {appConfig.GTM_ENABLED == 'true' && appConfig.GARAGE_GTM_CONTAINER_ID && (
                                                <link
                                                    rel="preload"
                                                    href={`https://www.googletagmanager.com/gtm.js?id=${appConfig.GARAGE_GTM_CONTAINER_ID}`}
                                                    as="script"
                                                />
                                            )}
                                            <link
                                                rel="preload"
                                                href={getAssetUrl('static/global.css')}
                                                as="style"
                                                {...{
                                                    onload:
                                                        "this.onload=null;this.rel='stylesheet'"
                                                }}
                                            />
                                            <noscript>{`<link rel="stylesheet" type="text/css" href="${getAssetUrl(
                                                'static/global.css'
                                            )}" />`}</noscript>
                                            {ACTIVE_DATA_ENABLED && (
                                                <script
                                                    src={getAssetUrl('static/head-active_data.js')}
                                                    id="headActiveData"
                                                    type="text/javascript"
                                                    defer
                                                ></script>
                                            )}
                                        </Helmet>
                                        <IntlProvider
                                            onError={(err) => {
                                                if (!messages) {
                                                    // During the ssr prepass phase the messages object has not loaded, so we can suppress
                                                    // errors during this time.
                                                    return
                                                }
                                                if (err.code === 'MISSING_TRANSLATION') {
                                                    // NOTE: Remove the console error for missing translations during development,
                                                    // as we knew translations would be added later.
                                                    logger.warn('Missing translation', {
                                                        namespace: 'App.IntlProvider',
                                                        additionalProperties: {
                                                            errorMessage: err.message
                                                        }
                                                    })
                                                    return
                                                }
                                                throw err
                                            }}
                                            locale={targetLocale}
                                            messages={messages}
                                            // For react-intl, the _default locale_ refers to the locale that the inline `defaultMessage`s are written for.
                                            // NOTE: if you update this value, please also update the following npm scripts in `template-retail-react-app/package.json`:
                                            // - "extract-default-translations"
                                            // - "compile-translations:pseudo"
                                            defaultLocale={DEFAULT_LOCALE}
                                        >
                                            <CurrencyProvider currency={currency}>
                                                <GlobalElementsProvider>
                                                    <UnavailableProductsInCartProvider>
                                                        <MiniCartProvider>
                                                            <Seo>
                                                                <meta
                                                                    name="viewport"
                                                                    content="width=device-width, initial-scale=1, minimum-scale=1"
                                                                />
                                                                <meta
                                                                    name="theme-color"
                                                                    content={THEME_COLOR}
                                                                />
                                                                {/* Favicons for all devices */}
                                                                <link
                                                                    rel="icon"
                                                                    type="image/x-icon"
                                                                    href={getAssetUrl(
                                                                        'static/ico/16x16_favicon_GRG.ico'
                                                                    )}
                                                                    sizes="16x16"
                                                                />
                                                                <link
                                                                    rel="icon"
                                                                    type="image/x-icon"
                                                                    href={getAssetUrl(
                                                                        'static/ico/32x32_favicon_GRG.ico'
                                                                    )}
                                                                    sizes="32x32"
                                                                />
                                                                <link
                                                                    rel="icon"
                                                                    type="image/x-icon"
                                                                    href={getAssetUrl(
                                                                        'static/ico/48x48_favicon_GRG.ico'
                                                                    )}
                                                                    sizes="48x48"
                                                                />
                                                                <link
                                                                    rel="apple-touch-icon"
                                                                    href={getAssetUrl(
                                                                        'static/img/global/apple-touch-icon.png'
                                                                    )}
                                                                    sizes="180x180"
                                                                />
                                                                <link
                                                                    rel="manifest"
                                                                    href={getAssetUrl(
                                                                        'static/manifest.json'
                                                                    )}
                                                                />
                                                            </Seo>

                                                            <ScrollToTop />

                                                            <Box
                                                                id="app"
                                                                display="flex"
                                                                flexDirection="column"
                                                                flex={1}
                                                            >
                                                                <SkipNavLink
                                                                    zIndex="skipLink"
                                                                    data-testid="skip-content"
                                                                >
                                                                    Skip to Content
                                                                </SkipNavLink>
                                                                <StoreLocatorModal
                                                                    isOpen={isOpenStoreLocator}
                                                                    onClose={onCloseStoreLocator}
                                                                />
                                                                {!isCheckout ? (
                                                                    <>
                                                                        <TopBar
                                                                            showCountrySelectorOnFooter={
                                                                                showCountrySelectorOnFooter
                                                                            }
                                                                        />
                                                                        <Box
                                                                            {...styles.headerWrapper}
                                                                        >
                                                                            <SearchModalProvider>
                                                                                <Header
                                                                                    onMenuClick={
                                                                                        onOpen
                                                                                    }
                                                                                    onLogoClick={
                                                                                        onLogoClick
                                                                                    }
                                                                                    onMyCartClick={
                                                                                        onCartClick
                                                                                    }
                                                                                    onMyAccountClick={
                                                                                        onAccountClick
                                                                                    }
                                                                                    onWishlistClick={
                                                                                        onWishlistClick
                                                                                    }
                                                                                    onStoreLocatorClick={
                                                                                        onOpenStoreLocator
                                                                                    }
                                                                                >
                                                                                    <HideOnDesktop>
                                                                                        <DrawerMenu
                                                                                            isOpen={
                                                                                                isOpen
                                                                                            }
                                                                                            onClose={
                                                                                                onClose
                                                                                            }
                                                                                            category={
                                                                                                categories?.[
                                                                                                    ROOT_CATEGORY_GARAGE
                                                                                                ]
                                                                                            }
                                                                                            onMyAccountClick={
                                                                                                onAccountClick
                                                                                            }
                                                                                            onLoyaltyClick={
                                                                                                onLoyaltyClick
                                                                                            }
                                                                                            onWishlistClick={
                                                                                                onWishlistClick
                                                                                            }
                                                                                            onGiftCardClick={
                                                                                                onGiftCardClick
                                                                                            }
                                                                                        />
                                                                                    </HideOnDesktop>

                                                                                    <HideOnMobile>
                                                                                        <ListMenu
                                                                                            root={
                                                                                                categories?.[
                                                                                                    ROOT_CATEGORY_GARAGE
                                                                                                ]
                                                                                            }
                                                                                            categories={
                                                                                                categories
                                                                                            }
                                                                                            itemsKey="categories"
                                                                                            itemsCountKey="onlineSubCategoriesCount"
                                                                                            contentComponent={
                                                                                                ListMenuContentWithData
                                                                                            }
                                                                                        />
                                                                                    </HideOnMobile>
                                                                                </Header>
                                                                            </SearchModalProvider>
                                                                        </Box>
                                                                    </>
                                                                ) : (
                                                                    // TODO: add dynamic status from live chat
                                                                    <CheckoutHeader
                                                                        isChatOnline={true}
                                                                    />
                                                                )}
                                                                {!isOnline && <OfflineBanner />}
                                                                <SkipNavContent
                                                                    style={{
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        flex: 1,
                                                                        outline: 0
                                                                    }}
                                                                >
                                                                    <Box
                                                                        as="main"
                                                                        id="app-main"
                                                                        role="main"
                                                                        display="flex"
                                                                        flexDirection="column"
                                                                        flex="1"
                                                                        position="relative"
                                                                    >
                                                                        <OfflineBoundary
                                                                            isOnline={false}
                                                                        >
                                                                            {children}
                                                                        </OfflineBoundary>
                                                                    </Box>
                                                                </SkipNavContent>

                                                                {!isCheckout ? (
                                                                    <Footer
                                                                        onShowCountrySelectorOnFooter={
                                                                            handleShowCountrySelectorOnFooter
                                                                        }
                                                                    />
                                                                ) : (
                                                                    <CheckoutFooter />
                                                                )}

                                                                <AuthModal {...authModal} />
                                                                <AccountModal
                                                                    {...accountModalProps}
                                                                />
                                                            </Box>
                                                        </MiniCartProvider>
                                                    </UnavailableProductsInCartProvider>
                                                </GlobalElementsProvider>
                                            </CurrencyProvider>
                                        </IntlProvider>
                                        {ACTIVE_DATA_ENABLED && (
                                            <script
                                                type="text/javascript"
                                                src={getAssetUrl('static/dwanalytics-22.2.js')}
                                                id="dwanalytics"
                                                defer
                                                onLoad={trackPage}
                                            ></script>
                                        )}
                                        {ACTIVE_DATA_ENABLED && (
                                            <script
                                                src={getAssetUrl('static/dwac-21.7.js')}
                                                type="text/javascript"
                                                id="dwac"
                                                defer
                                            ></script>
                                        )}
                                        <CustomToast />
                                    </CustomToastProvider>
                                    {isSignifydReady && isCheckout && <SignifydScript sessionId={basket?.c_SignifydOrderSessionId} />}
                                </HeaderProvider>
                            </StickyProvider>
                        </AccountEditProvider>
                    </CategoryUrlMapProvider>
                </GoogleReCaptchaProvider>
            </StorefrontPreview>
            <PreviewModeProjectSelector />
        </Box>
    )
}

App.propTypes = {
    children: PropTypes.node
}

export default App
